import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { Cue5050 } from '../../_models/fundraising/Cue5050';
import { Cue5050Service } from './cue5050.service';
import { GraphBar } from '../../_models/graph/GraphBar';
import { BreakdownType, Cue5050BaseReport, ReportType } from '../../_models/fundraising/reports/Cue5050BaseReport';
import { getUrl } from '../../_shared/utils';
import { JWTTokenService } from '../jwtToken.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Cue5050FinancialReport } from '../../_models/fundraising/reports/Cue5050FinancialReport';
import { Cue5050PlayersReport } from '../../_models/fundraising/reports/Cue5050PlayersReport';
import { Cue5050PurchaseDetailsReport } from '../../_models/fundraising/reports/Cue5050PurchaseDetailsReport';
import { SavedReport } from '../../_models/fundraising/reports/SavedReport';
import { BaseService } from '../../_shared/base.service';

@Injectable()
export class FundraisingReportService extends BaseService {

    private _events: Cue5050[] = [];
    private _eventIds: number[] = [];

    private _eventsForReport: Cue5050[] = [];

    private _reportType: ReportType;
    private _initialBreakdownType: BreakdownType;

    private _barData: Cue5050BaseReport[];

    public apiUrl = getUrl();

    constructor(
        private cue5050Service: Cue5050Service,
        protected http: HttpClient,
        protected jwtTokenService: JWTTokenService
    ){
        super(http, jwtTokenService);
    }
    
    get eventsForReport() {
        return this._eventsForReport;
    }

    get events(){
        return this._events;
    }
    set events (events: Cue5050[]){
        if (!events)
            this._events = [];

        this._events = events;
    }

    get eventIds(){
        return this._eventIds;
    }
    set eventIds (ids: number[]){
        if (!ids)
            this._eventIds = [];

        this._eventIds = ids;
        this._eventsForReport = this._events.filter(e => this._eventIds.includes(e.cue5050Id));
    }

    get reportType(){
        return this._reportType;
    }
    set reportType (type: ReportType){
        if (!type)
            this._reportType = ReportType.FinancialReport;

        this._reportType = type;
    }

    get breakdownType(){
        return this._initialBreakdownType;
    }
    set breakdownType(type: BreakdownType){
        this._initialBreakdownType = type;
    }

    public getBarData(): Observable<Cue5050BaseReport[]>{
        return new Observable<Cue5050BaseReport[]>(subcriber => {
            let reportObs = of(null);

            switch(this._reportType){
                case ReportType.FinancialReport:
                    reportObs = this.getFinancialData(this._eventsForReport.map(e => e.cue5050Id));
                    break;
                case ReportType.WhosPlaying:
                    reportObs = this.getWhosPlaying(this._eventsForReport.map(e => e.cue5050Id));
                    break;
                case ReportType.PurchaseDetails:
                    reportObs = this.getPurchaseData(this._eventsForReport.map(e => e.cue5050Id));
                    break;
                default:
                    break;
            }

            reportObs
                .subscribe(data => {
                    if (!data || data?.length < 1){
                        subcriber.error('No report data found.');
                    }
                    this._barData = data;
                    subcriber.next(this._barData);
                    subcriber.complete();
                });
        });
    }

    public getBarMappingByBreakdownType(breakdownType: BreakdownType): GraphBar[]{
        if (!this._barData || this._barData?.length < 1){
            throw new Error("No report data found.");
        }
        return this._barData.map(d => d.getBarMappingsByBreakdownType(breakdownType));
    }

    public getBreakDownTypes(): BreakdownType[]{
        if (!this._barData || this._barData?.length < 1){
            throw new Error("No report data found.");
        }
        return this._barData[0].getBreakdownTypes();
    }

    public getBreakdownByBreakdownType(type: BreakdownType): string[]{
        if (!this._barData || this._barData?.length < 1){
            throw new Error("No report data found.");
        }else if (type === BreakdownType.OrdersByTicketTier){
            var allBreakdowns = this._barData.map(d => d.getBreakdownByBreakdownType(type).length);
            var mostTiersIdx = 0;
            for (let i = 0; i < allBreakdowns.length; i++){
                if (allBreakdowns[i] > mostTiersIdx)
                    mostTiersIdx = i;
            }
            return this._barData[mostTiersIdx].getBreakdownByBreakdownType(type);
        }
        return this._barData[0].getBreakdownByBreakdownType(type);
    }

    public getXAxisName(): string{
        if (!this._barData || this._barData?.length < 1){
            throw new Error("No report data found.");
        }
        return this._barData[0].getXAxisName();
    }

    public getYAxisName(): string{
        if (!this._barData || this._barData?.length < 1){
            throw new Error("No report data found.");
        }
        return this._barData[0].getYAxisName();
    }

    public getBreakdownDescription(type: BreakdownType){
        switch(type){
            case BreakdownType.SalesByPaymentMethod:
                return "Gross sales by payment method";
            case BreakdownType.SalesByFeesAndNet:
                return "Gross sales by fees and net";
            case BreakdownType.SalesByStation:
                return "Gross sales by stations and online";
            case BreakdownType.PlayersByReturning:
                return "Number of players by new and returning";
            case BreakdownType.OrdersByPaymentMethod:
                return "Orders by payment method";
            case BreakdownType.OrderByStation:
                return "Orders by stations and online";
            case BreakdownType.OrdersByTicketTier:
                return "Order by Ticket Tier";
            default:
                return "Invalid breakdown type";
        }
    }

    public getReportTypeDescription(type: ReportType) {
        switch(type) {
            case ReportType.FinancialReport:
                return 'Financial Report';
            case ReportType.WhosPlaying:
                return "Who's Playing?";
            case ReportType.PurchaseDetails:
                return "Purchase Details";
            default:
                return 'Invalid report type';
        }
    }

    public getSavedReports(): Observable<SavedReport[]> {
        return this.http.get<SavedReport[]>(getUrl('fundraising/reports'), this.requestOptionsAuthenticated('portal'));
    }

    public getReportById(reportId: number): Observable<SavedReport> {
        return this.http.get<SavedReport>(getUrl(`fundraising/reports/${reportId}`), this.requestOptionsAuthenticated('portal'));
    }

    public saveReport(report: any): Observable<any> {
        return this.http.put<any>(getUrl('fundraising/reports/save-report'), report, this.requestOptionsAuthenticated('portal'));
    }

    public getFinancialData(ids: number[]): Observable<Cue5050FinancialReport[]> {
        return this.http.post<Cue5050FinancialReport[]>(getUrl('fundraising/reports/financial'), ids, this.requestOptionsAuthenticated('portal'))
          .pipe(map(res => res.map(d => new Cue5050FinancialReport(d))));
      }
  
      public getPurchaseData(ids: number[]): Observable<Cue5050PurchaseDetailsReport[]> {
        return this.http.post<Cue5050PurchaseDetailsReport[]>(getUrl('fundraising/reports/purchase-details'), ids, this.requestOptionsAuthenticated('portal'))
          .pipe(map(res => res.map(d => new Cue5050PurchaseDetailsReport(d))));
      }
  
      public getWhosPlaying(ids: number[]): Observable<Cue5050PlayersReport[]> {
        return this.http.post<Cue5050PlayersReport[]>(getUrl('fundraising/reports/whos-playing'), ids, this.requestOptionsAuthenticated('portal'))
          .pipe(map(res => res.map(d => new Cue5050PlayersReport(d))));
      }
  
      public getCSVReport(reportType: ReportType, ids: number[]): Observable<File> {
        let headers = this.requestOptionsAuthenticated('portal');
        return this.http.post<File>(getUrl('fundraising/reports/csv'), {reportType: reportType, eventIds: ids}, {headers: headers.headers, responseType: 'blob' as 'json'});
      }

    private requestOptionsAuthenticated(tokenKey: string) {
        const requestOptions = {
            headers: new HttpHeaders()
                .set('Content-type', 'application/json')
                .append('Authorization', 'Bearer ' + this.jwtTokenService.getToken(tokenKey)),
        };
        return requestOptions;
    }
}
